import React, { useEffect, useState } from 'react';
import { X, UserCog } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

// Maps a resourceType (matches RecordShare's enum) to the update endpoint
// that already exists for that entity. Every one of these controllers
// spreads req.body wholesale into the update and already computes/logs a
// before/after diff (src/utils/recordVisibility.js's sibling, the generic
// AuditLogService), so posting { ownerId } here is enough to both change
// the owner AND get a real audit trail entry for free — no backend change
// needed for this feature.
const ENDPOINTS = {
  lead: (id) => `/crm/leads/${id}`,
  deal: (id) => `/crm/deals/${id}`,
  account: (id) => `/crm/accounts/${id}`,
  contact: (id) => `/crm/contacts/${id}`,
  task: (id) => `/crm/tasks/${id}`,
};

export default function TransferOwnershipModal({ isOpen, onClose, resourceType, resourceId, currentOwnerId, onTransferred }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api.get('/auth/team-members')
      .then(res => setTeamMembers(res.data.users || []))
      .catch(() => alertService.error('Error', 'Failed to load teammates'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTransfer = async () => {
    if (!selectedUserId) return alertService.error('Error', 'Select a new owner first');
    const buildUrl = ENDPOINTS[resourceType];
    if (!buildUrl) return alertService.error('Error', `Unsupported resource type "${resourceType}"`);
    setSaving(true);
    try {
      await api.put(buildUrl(resourceId), { ownerId: selectedUserId });
      alertService.success('Ownership Transferred', 'The record now belongs to the selected teammate');
      onTransferred?.(selectedUserId);
      onClose();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to transfer ownership');
    } finally {
      setSaving(false);
    }
  };

  const availableUsers = teamMembers.filter(u => u._id !== currentOwnerId);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E] flex items-center gap-2">
            <UserCog size={16} className="text-[#0176D3]" /> Transfer Ownership
          </h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {loading ? (
            <p className="text-[13px] text-[#706E6B]">Loading teammates…</p>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">New Owner</label>
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                  className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                >
                  <option value="">Select teammate…</option>
                  {availableUsers.map(u => (
                    <option key={u._id} value={u._id}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[#706E6B]">The new owner gains full access; this action is logged in the audit trail.</p>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={onClose} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button
                  type="button"
                  onClick={handleTransfer}
                  disabled={saving || !selectedUserId}
                  className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50"
                >
                  {saving ? 'Transferring…' : 'Transfer'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
